import { observer } from 'mobx-react';
import { useMemo, useState } from 'react';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { PRODUCT_TYPE } from 'src/types/product';
import { stringTransform } from 'src/utils/helpers';
import { floatNumbersValidation } from 'src/utils/validation';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import { TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import CustomModalComponent from 'src/view/components/Controls/Modal/Modal';
import ModalBackground from 'src/view/components/Controls/Modal/ModalBackground/ModalBackground';
import ModalContent from 'src/view/components/Controls/Modal/ModalContent/ModalContent';
import ModalFooter from 'src/view/components/Controls/Modal/ModalFooter/ModalFooter';
import ModalHeader from 'src/view/components/Controls/Modal/ModalHeader/ModalHeader';
import ItemDetailsTable from 'src/view/containers/Modals/MenuGroupItemModal/components/ItemDetailsTable/ItemDetailsTable';
import ItemPrice from 'src/view/containers/Modals/MenuGroupItemModal/components/ItemPrice/ItemPrice';
import TooltipIcon from '~/view/components/TooltipIcon/TooltipIcon';

const MenuGroupItemModal = (): JSX.Element | null => {
  const item = modalStore.menuGroupItemModal!.item;
  const isTaker360Enabled = modalStore.menuGroupItemModal!.isTaker360Enabled;
  const modifiers = modalStore.modifiers;

  const [taker360Price, setTaker360Price] = useState<string>(item.menu_group_price ?? item.price);

  const isItemWithSizes = !!item.sizes.length;
  const isItemWithOptions = item.modifiers.some((modifier) => modifier.options.length);
  const isDisabledPriceChange = item.type === PRODUCT_TYPE.DEAL_COMBO || item.type === PRODUCT_TYPE.MEAL_COMBO;

  const options = useMemo(() => {
    if (!isItemWithOptions || !modifiers?.length) return null;

    return (
      <>
        <h2 className="family-regular text-size-18 letter-spacing-12 text-uppercase color-gray2 mt-6">{localeStore.t('option_prices')}</h2>
        {modifiers.map((modifier, index) => (
          <ItemDetailsTable
            name={localeStore.t('option_name')}
            details={modifier.options}
            isFirstElement={index === 0}
            showSeparator={index !== modifiers.length - 1}
            modifierName={modifier.name[localeStore.language]}
            isTaker360Enabled={isTaker360Enabled && !isItemWithSizes}
          />
        ))}
      </>
    );
  }, [isItemWithOptions, modifiers, isTaker360Enabled, isItemWithSizes]);

  const onPriceChange = (value: string) => {
    const price = stringTransform(value);
    if (floatNumbersValidation(price)) setTaker360Price(price);
  };

  const onSave = () => {
    modalStore.menuGroupItemModal?.onPriceSave?.(taker360Price);
    modalStore.closeMenuGroupItemModal();
  };

  return (
    <CustomModalComponent modalMaxWidth={743} isOpen withCloseIcon onRequestClose={modalStore.closeMenuGroupItemModal}>
      <ModalBackground>
        <ModalHeader title={localeStore.t(isItemWithSizes || !isTaker360Enabled ? 'item_details_popup' : 'change_price_taker360_popup')} />
        <ModalContent scrollSize="small">
          <div className="d-flex align-items-center">
            <h2 className="family-regular text-size-18 letter-spacing-12 text-uppercase color-gray2">{localeStore.t('item_prices')}</h2>
            {isItemWithSizes && <TooltipIcon message="item_with_sizes_taker360_price" position={TOOLTIP_POSITION.PORTAL} iconClassName="ml-3" />}
          </div>
          {isItemWithSizes ? (
            <ItemDetailsTable isFirstElement name={localeStore.t('size_name')} details={item.sizes} />
          ) : (
            <ItemPrice
              price={item.price}
              taker360Price={taker360Price}
              isTaker360Enabled={isTaker360Enabled}
              isDisabledPriceChange={isDisabledPriceChange}
              onChange={onPriceChange}
            />
          )}
          {options}
        </ModalContent>
        <ModalFooter>
          {!isItemWithSizes && isTaker360Enabled ? <Button title={localeStore.t('Save')} onClick={onSave} size="large" /> : null}
        </ModalFooter>
      </ModalBackground>
    </CustomModalComponent>
  );
};

export default observer(MenuGroupItemModal);
